<template>
  <!-- Global popup, opened from the TopBar on every route (the editor too)
       through useAccountSettingsModal. -->
  <BaseDialog
    :open="isOpen"
    :labelled-by="titleId"
    panel-class="max-w-2xl max-h-[90vh] rounded-xl flex flex-col overflow-hidden"
    @close="close"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-border">
      <div>
        <h2 :id="titleId" class="text-lg font-semibold text-text">Account settings</h2>
        <p class="text-xs text-muted mt-0.5">Manage your profile and security.</p>
      </div>
      <button
        type="button"
        class="w-[34px] h-[34px] rounded-[10px] text-muted flex items-center justify-center hover:bg-canvas hover:text-text transition-colors"
        aria-label="Close"
        @click="close"
      >
        <X :size="20" />
      </button>
    </div>

    <!-- Scrollable body -->
    <div class="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
      <!-- Profile -->
      <section class="space-y-3">
        <h3 class="text-[11px] font-semibold text-muted uppercase tracking-wide">Profile</h3>

        <div class="flex items-center gap-4">
          <div class="relative w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-xl font-semibold overflow-hidden shrink-0">
            <img v-if="user?.avatar_url" :src="user.avatar_url" alt="Avatar" class="w-full h-full object-cover" />
            <span v-else>{{ avatarInitial }}</span>
          </div>
          <div class="flex flex-col gap-1.5">
            <input
              ref="avatarInputRef"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="hidden"
              @change="onAvatarSelected"
            />
            <div class="flex items-center gap-3">
              <button
                type="button"
                :disabled="avatarUploading"
                class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
                @click="avatarInputRef?.click()"
              >
                <Loader2 v-if="avatarUploading" :size="14" class="animate-spin" />
                {{ user?.avatar_url ? 'Replace photo' : 'Upload photo' }}
              </button>
              <button
                v-if="user?.avatar_url"
                type="button"
                :disabled="avatarUploading"
                class="text-xs text-muted hover:text-error underline"
                @click="removeAvatar"
              >
                Remove
              </button>
            </div>
            <p class="text-xs text-muted/70">JPG, PNG or WEBP · up to 10MB</p>
          </div>
        </div>

        <form class="space-y-3" @submit.prevent="saveProfile">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField v-model="firstName" label="First name" maxlength="100" autocomplete="given-name" :reserve-error-space="false" />
            <FormField v-model="lastName" label="Last name" maxlength="100" autocomplete="family-name" :reserve-error-space="false" />
          </div>

          <div>
            <p class="block text-xs font-medium text-muted mb-1.5">Email</p>
            <div class="flex items-center gap-2 px-3 py-2 border border-border rounded-[10px] bg-bg text-sm text-muted">
              <span class="truncate">{{ user?.email }}</span>
              <span
                v-if="user?.email_verified"
                class="ml-auto shrink-0 text-[11px] font-medium text-success bg-success-bg rounded px-1.5 py-0.5"
              >Verified</span>
            </div>
          </div>

          <div class="flex justify-end pt-1">
            <button
              type="submit"
              :disabled="!profileDirty || profileSaving"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
            >
              <Loader2 v-if="profileSaving" :size="14" class="animate-spin" />
              Save changes
            </button>
          </div>
        </form>
      </section>

      <div class="h-px bg-border"></div>

      <!-- Password -->
      <section class="space-y-3">
        <div>
          <h3 class="text-[11px] font-semibold text-muted uppercase tracking-wide">Password</h3>
          <p v-if="!user?.has_password" class="text-sm text-muted mt-1">
            Add a password so you can sign in with email.
          </p>
        </div>

        <form class="space-y-3" @submit.prevent="submitPassword">
          <FormField
            v-if="user?.has_password"
            v-model="currentPassword"
            label="Current password"
            type="password"
            autocomplete="current-password"
            :error="passwordErrors.current"
          >
            <template #meta>
              <span v-if="resetLinkSent" class="shrink-0 text-muted">Reset link sent — check your email.</span>
              <button
                v-else
                type="button"
                :disabled="sendingResetLink"
                class="shrink-0 text-primary hover:underline disabled:opacity-50"
                @click="sendResetLink"
              >
                {{ sendingResetLink ? 'Sending…' : 'Forgot password?' }}
              </button>
            </template>
          </FormField>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField
              v-model="newPassword"
              label="New password"
              type="password"
              autocomplete="new-password"
              :error="passwordErrors.new"
              :reserve-error-space="false"
            />
            <FormField
              v-model="confirmPassword"
              label="Confirm new password"
              type="password"
              autocomplete="new-password"
              :error="passwordErrors.confirm"
              :reserve-error-space="false"
            />
          </div>

          <p class="text-xs text-muted/70">At least 8 characters, with upper, lower, and a digit.</p>

          <div v-if="passwordServerError" class="px-3 py-2 rounded-[10px] bg-error-bg text-error text-xs">
            {{ passwordServerError }}
          </div>

          <div class="flex justify-end">
            <button
              type="submit"
              :disabled="passwordSaving"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
            >
              <Loader2 v-if="passwordSaving" :size="14" class="animate-spin" />
              {{ user?.has_password ? 'Change password' : 'Add password' }}
            </button>
          </div>
        </form>
      </section>

      <div class="h-px bg-border"></div>

      <!-- Google -->
      <section class="space-y-3">
        <h3 class="text-[11px] font-semibold text-muted uppercase tracking-wide">Google</h3>
        <div class="flex items-center gap-3 p-3 border border-border rounded-[10px]">
          <div class="w-9 h-9 rounded-full bg-bg border border-border flex items-center justify-center text-xs font-semibold text-text">G</div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-text">Google account</p>
            <p class="text-xs text-muted truncate">
              {{ user?.google_connected ? 'Connected — sign in with Google' : 'Not connected' }}
            </p>
          </div>
          <button
            v-if="user?.google_connected"
            type="button"
            :disabled="!user?.has_password || disconnectingGoogle"
            :title="!user?.has_password ? 'Add a password first' : undefined"
            class="px-3 py-1.5 text-xs font-medium text-text border border-border rounded-lg hover:bg-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="disconnectGoogle"
          >
            {{ disconnectingGoogle ? 'Disconnecting…' : 'Disconnect' }}
          </button>
          <button
            v-else
            type="button"
            class="px-3 py-1.5 text-xs font-medium text-text border border-border rounded-lg hover:bg-bg transition-colors"
            @click="connectGoogle"
          >
            Connect
          </button>
        </div>
        <p v-if="user?.google_connected && !user?.has_password" class="text-xs text-muted">
          Add a password first so you can still sign in.
        </p>
      </section>

      <div class="h-px bg-border"></div>

      <!-- Delete account -->
      <section class="space-y-3">
        <h3 class="text-[11px] font-semibold text-muted uppercase tracking-wide">Delete account</h3>
        <div class="flex items-center justify-between gap-4">
          <p class="text-sm text-muted min-w-0">Permanently deletes your account, videos, and uploads.</p>
          <button
            type="button"
            class="shrink-0 px-3 py-1.5 text-xs font-medium text-error border border-error/40 rounded-lg hover:bg-error-bg transition-colors"
            @click="confirmingDelete = true"
          >
            Delete account
          </button>
        </div>
      </section>
    </div>

    <ConfirmDialog
      :open="confirmingDelete"
      title="Delete account"
      message="This permanently deletes your account and every video. It can't be undone."
      confirm-label="Delete my account"
      type-to-confirm="DELETE"
      :busy="deleting"
      :error="deleteServerError"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </BaseDialog>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { Loader2, X } from 'lucide-vue-next'
import { extractApiError } from '@/services/api'
import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth'
import { useAccountSettingsModal } from '@/composables/useAccountSettingsModal'
import { stashRedirect } from '@/router'
import BaseDialog from '@/components/BaseDialog.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import FormField from '@/components/FormField.vue'
import { passwordStrengthError } from '@/components/auth/validators'

const MAX_AVATAR_BYTES = 10_000_000
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const { isOpen, close } = useAccountSettingsModal()
const user = computed(() => authStore.user)
const titleId = useId()

const firstName = ref(user.value?.first_name ?? '')
const lastName = ref(user.value?.last_name ?? '')
const profileSaving = ref(false)

// Reset local form state whenever the modal opens or the user changes
watch(
  [isOpen, () => user.value?.first_name, () => user.value?.last_name],
  ([open, f, l]) => {
    if (!open) return
    firstName.value = f ?? ''
    lastName.value = l ?? ''
    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
    passwordErrors.value = {}
    passwordServerError.value = ''
    resetLinkSent.value = false
    cancelDelete()
  },
)

const profileDirty = computed(() => {
  return (
    (firstName.value || '') !== (user.value?.first_name ?? '') ||
    (lastName.value || '') !== (user.value?.last_name ?? '')
  )
})

const avatarInputRef = ref<HTMLInputElement | null>(null)
const avatarUploading = ref(false)

const avatarInitial = computed(() => {
  const f = user.value?.first_name?.trim()
  if (f) return f[0].toUpperCase()
  const email = user.value?.email
  return email ? email[0].toUpperCase() : '?'
})

async function saveProfile() {
  profileSaving.value = true
  try {
    await authStore.updateProfile({
      first_name: firstName.value.trim() || null,
      last_name: lastName.value.trim() || null,
    })
    toast.success('Profile saved')
  } catch (err) {
    toast.error(extractApiError(err, 'Failed to save profile'))
  } finally {
    profileSaving.value = false
  }
}

async function onAvatarSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return

  if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
    toast.error('Please choose a JPG, PNG, or WEBP image')
    return
  }
  if (file.size > MAX_AVATAR_BYTES) {
    toast.error('Image is larger than 10MB')
    return
  }

  avatarUploading.value = true
  try {
    await authStore.uploadAvatar(file)
    toast.success('Photo updated')
  } catch (err) {
    toast.error(extractApiError(err, 'Failed to upload photo'))
  } finally {
    avatarUploading.value = false
  }
}

async function removeAvatar() {
  avatarUploading.value = true
  try {
    await authStore.deleteAvatar()
    toast.success('Photo removed')
  } catch (err) {
    toast.error(extractApiError(err, 'Failed to remove photo'))
  } finally {
    avatarUploading.value = false
  }
}

// Password section
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const passwordErrors = ref<{ current?: string; new?: string; confirm?: string }>({})
const passwordServerError = ref('')
const passwordSaving = ref(false)
const sendingResetLink = ref(false)
const resetLinkSent = ref(false)

async function sendResetLink() {
  if (!user.value?.email) return
  sendingResetLink.value = true
  try {
    await authService.forgotPassword({ email: user.value.email })
    resetLinkSent.value = true
  } catch (err) {
    toast.error(extractApiError(err, 'Failed to send reset link'))
  } finally {
    sendingResetLink.value = false
  }
}

async function submitPassword() {
  passwordErrors.value = {}
  passwordServerError.value = ''

  if (user.value?.has_password && !currentPassword.value) {
    passwordErrors.value.current = 'Current password is required'
  }
  const newErr = passwordStrengthError(newPassword.value, true)
  if (newErr) passwordErrors.value.new = newErr
  if (newPassword.value !== confirmPassword.value) {
    passwordErrors.value.confirm = 'Passwords do not match'
  }
  if (Object.keys(passwordErrors.value).length > 0) return

  passwordSaving.value = true
  try {
    if (user.value?.has_password) {
      await authStore.changePassword({
        current_password: currentPassword.value,
        new_password: newPassword.value,
      })
      toast.success('Password updated')
    } else {
      await authStore.setPassword({ new_password: newPassword.value })
      toast.success('Password set')
    }
    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
  } catch (err) {
    passwordServerError.value = extractApiError(err, 'Failed to update password')
  } finally {
    passwordSaving.value = false
  }
}

// Google account
const disconnectingGoogle = ref(false)

async function disconnectGoogle() {
  if (!user.value?.has_password) return
  disconnectingGoogle.value = true
  try {
    await authStore.disconnectGoogle()
    toast.success('Google disconnected')
  } catch (err) {
    toast.error(extractApiError(err, 'Failed to disconnect Google'))
  } finally {
    disconnectingGoogle.value = false
  }
}

function connectGoogle() {
  // Come back to the page the user was on, not the library.
  stashRedirect(route.fullPath)
  window.location.href = authService.getGoogleLoginUrl()
}

// Delete account
const confirmingDelete = ref(false)
const deleteServerError = ref('')
const deleting = ref(false)

function cancelDelete() {
  confirmingDelete.value = false
  deleteServerError.value = ''
}

async function confirmDelete() {
  deleting.value = true
  deleteServerError.value = ''
  try {
    await authStore.deleteAccount()
    cancelDelete()
    close()
    toast.success('Account deleted')
    router.push('/login')
  } catch (err) {
    deleteServerError.value = extractApiError(err, 'Failed to delete account')
  } finally {
    deleting.value = false
  }
}
</script>
